import { getNumber, normalizeText } from '@utils/Format'
import { compareIndexOf } from '@utils/Compare'

/**
 * Método padrão para filtro do v-data-table
 * @param {Array} items Default - lista de dados
 * @param {String} index Default - coluna
 * @param {Array} isDec Default - ordem
 * @param {Object} dict Obrigatório - Dicionário com colunas a serem filtradas.
 *                      {texts, numbers, dates}
 */
export const customSort = (items, index, isDec, dict) => {
  let data = items
  index = index.reverse()[0]
  isDec = isDec[0]
  if (dict['texts'] && dict['texts'].includes(index)) {
    data = data.sort((a, b) => {
      return isDec
        ? (b[index] || 'zzzz').localeCompare(a[index] || 'zzzz')
        : (a[index] || 'zzzz').localeCompare(b[index] || 'zzzz')
    })
  } else if (dict['numbers'] && dict['numbers'].includes(index)) {
    data = data.sort((a, b) => {
      return isDec
        ? getNumber(b[index]) - getNumber(a[index])
        : getNumber(a[index]) - getNumber(b[index])
    })
  } else if (dict['dates'] && dict['dates'].includes(index)) {
    data = data.sort((a, b) => {
      const convert = /(\d{2})\/(\d{2})\/(\d{4})/
      const erroB =
        new Date(
          String(b[index] || '').replace(convert, '$3-$2-$1')
        ).toString() === 'Invalid Date'
      const erroA =
        new Date(
          String(a[index] || '').replace(convert, '$3-$2-$1')
        ).toString() === 'Invalid Date'
      if (erroA && erroB) {
        return 0
      }
      if (erroB) {
        return -1
      }
      if (erroA) {
        return 1
      }
      return isDec
        ? new Date(String(b[index] || '').replace(convert, '$3-$2-$1')) -
            new Date(String(a[index] || '').replace(convert, '$3-$2-$1'))
        : new Date(String(a[index] || '').replace(convert, '$3-$2-$1')) -
            new Date(String(b[index] || '').replace(convert, '$3-$2-$1'))
    })
  } else {
    data = data.sort((a, b) => {
      if (isDec) {
        return a[index] > b[index] ? -1 : 1
      }
      return a[index] > b[index] ? 1 : -1
    })
  }

  return data
}

/**
 * Ordena uma lista levando em consideração uma lista base que deve ficar no começo.
 * @param {Array} items Itens para ordenação
 * @param {Array} base Itens para base da ordenação. Estes itens vão no começo da lista final.
 * @returns {Array} Lista principal ordenada.
 */
export const baseSort = (items = [], base = []) => {
  return items.sort((a, b) => {
    return compareIndexOf(a, b, base)
  })
}

/**
 * Ordenar uma lista de datas. Precisa estar em string no formato brasileiro.
 * @param items Lista de datas. [str]
 * @returns {Array} Lista de datas ordenada. [str]
 */
export const sortDates = (items) => {
  return items.sort((a, b) => {
    let [aDay, aMonth, aYear] = a.split('/')
    let [bDay, bMonth, bYear] = b.split('/')
    return (
      new Date(aYear, aMonth, aDay).getTime() - new Date(bYear, bMonth, bDay)
    )
  })
}

/**
 * Retornando a lista de dados ordanado por data informada na key.
 * @param items
 * @param key
 * @returns {*}
 */
export const sortByDate = ({ items, key }) => {
  if (items && Array.isArray(items)) {
    return items.sort((a, b) => {
      const convert = /(\d{2})\/(\d{2})\/(\d{4})/
      const valB = new Date(
        (b[key] || b || '').toString().replace(convert, '$3-$2-$1')
      )
      const erroB = valB.toString() === 'Invalid Date'
      const valA = new Date(
        (a[key] || a || '').toString().replace(convert, '$3-$2-$1')
      )
      const erroA = valA.toString() === 'Invalid Date'
      if (erroA && erroB) {
        return 0
      }
      if (erroB) {
        return -1
      }
      if (erroA) {
        return 1
      }
      return valB - valA
    })
  }
  return []
}

export const sortByDateReverse = ({ items, key }) => {
  if (items && Array.isArray(items)) {
    return items.sort((a, b) => {
      const convert = /(\d{2})\/(\d{2})\/(\d{4})/
      const valB = new Date(
        (b[key] || b || '').toString().replace(convert, '$3-$2-$1')
      )
      const erroB = valB.toString() === 'Invalid Date'
      const valA = new Date(
        (a[key] || a || '').toString().replace(convert, '$3-$2-$1')
      )
      const erroA = valA.toString() === 'Invalid Date'
      if (erroA && erroB) {
        return 0
      }
      if (erroB) {
        return -1
      }
      if (erroA) {
        return 1
      }
      return valA - valB
    })
  }
  return []
}

export const sortClientsByName = (items = [], { activeKey = 'ativo' } = {}) => {
  if (!Array.isArray(items)) return []

  const isInactiveClient = (client) => {
    if (!client || !Object.prototype.hasOwnProperty.call(client, activeKey)) {
      return false
    }
    return !client[activeKey]
  }

  const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' })
  const decorated = items.map((client) => {
    const name =
      client && (client.name || client.nome_cliente)
        ? client.name || client.nome_cliente
        : ''
    return {
      client,
      inactive: isInactiveClient(client),
      normalizedName: normalizeText(name),
    }
  })

  decorated.sort((itemA, itemB) => {
    if (itemA.inactive !== itemB.inactive) {
      return itemA.inactive ? 1 : -1
    }

    return collator.compare(itemA.normalizedName, itemB.normalizedName)
  })

  return decorated.map((item) => item.client)
}
